import React, { useState } from 'react';
import { GripVertical, Copy, Trash2, ChevronUp, ChevronDown, AlertCircle, Info } from 'lucide-react';
import { ErrorRule, RuleMatch, RuleAction, RuleOnExhausted } from '../../../../types';
import { classifyStatusToken, summarizeRule, RuleSetIssue } from '../../../../utils/errorRuleset';
import { INPUT_CLASS, LABEL_CLASS } from './uiConstants';
import { ChipInput } from './ChipInput';

interface RuleCardProps {
  rule: ErrorRule;
  index: number;
  total: number;
  collapsed: boolean;
  issues: RuleSetIssue[]; // issues for THIS rule index
  onChange: (rule: ErrorRule) => void;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggleCollapse: () => void;
}

const BACKOFF_CAP_PRESETS = [
  { label: '2 min', value: 120000 },
  { label: '10 min', value: 600000 },
  { label: '1 hour', value: 3600000 },
  { label: '1 day', value: 86400000 },
];

const STATUS_PRESETS = [
  { label: '4xx', value: '400-499' },
  { label: '5xx', value: '500-599' },
  { label: '408', value: '408' },
  { label: '429', value: '429' },
];

const EXCEPTION_SUGGESTIONS = ['Aggregate.NotFound', 'Aggregate.AlreadyExists', 'Aggregate.VersionMismatch', '*.NotFound', '*.AlreadyExists', '*.VersionMismatch'];

export const RuleCard: React.FC<RuleCardProps> = ({
  rule, index, total, collapsed, issues, onChange, onMove, onDuplicate, onDelete, onToggleCollapse,
}) => {
  const [showBackoff, setShowBackoff] = useState(!!(rule.action.disposition === 'retry' && rule.action.backoff));
  const match = rule.match || {};
  const action = rule.action;
  const errors = issues.filter(i => i.severity === 'error');

  const setMatch = (patch: Partial<RuleMatch>) => onChange({ ...rule, match: { ...match, ...patch } });
  const setAction = (next: RuleAction) => onChange({ ...rule, action: next });

  // ---- match handlers ----
  const toggleBodyPredicate = (on: boolean) => {
    if (on) setMatch({ bodyPredicate: { kind: 'jsonpath', expression: '', op: 'exists' } });
    else { const m = { ...match }; delete m.bodyPredicate; onChange({ ...rule, match: m }); }
  };

  // ---- action handlers ----
  const changeDisposition = (d: 'retry' | 'ignore' | 'throw') => {
    if (d === action.disposition) return;
    if (d === 'retry') setAction({ disposition: 'retry', maxAttempts: 0 });
    else if (d === 'ignore') setAction({ disposition: 'ignore' });
    else setAction({ disposition: 'throw', errorCode: '' });
  };

  const setMaxAttempts = (n: number) => {
    if (action.disposition !== 'retry') return;
    const next: RuleAction = { ...action, maxAttempts: n };
    if (n === 0) delete (next as any).onExhausted;
    else if (!next.onExhausted) next.onExhausted = { disposition: 'throw', errorCode: '' };
    setAction(next);
  };

  const setOnExhausted = (oe: RuleOnExhausted) => {
    if (action.disposition !== 'retry') return;
    setAction({ ...action, onExhausted: oe });
  };

  const setBackoff = (patch: Partial<NonNullable<Extract<RuleAction, { disposition: 'retry' }>['backoff']>>) => {
    if (action.disposition !== 'retry') return;
    const base = action.backoff || { baseMs: 1000, factor: 5, capMs: 86400000, jitter: 0.25 };
    setAction({ ...action, backoff: { ...base, ...patch } });
  };

  const summary = summarizeRule(rule);

  return (
    <div className={`rounded-xl border ${errors.length ? 'border-red-300 dark:border-red-800' : 'border-slate-200 dark:border-slate-800'} bg-white dark:bg-slate-900/40`}>
      {/* header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800">
        <GripVertical size={14} className="text-slate-300 shrink-0" />
        <span className="text-xs font-bold text-slate-500 shrink-0">Rule {index + 1}</span>
        {collapsed && (
          <span className="text-xs text-slate-500 truncate flex-1">
            <span className="text-slate-400">WHEN</span> {summary.when} <span className="text-slate-400">THEN</span> {summary.then}
          </span>
        )}
        <div className="flex items-center gap-1 ml-auto shrink-0 text-slate-400">
          {errors.length > 0 && <AlertCircle size={14} className="text-red-500" />}
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="p-1 hover:text-slate-700 disabled:opacity-30" title="Move up"><ChevronUp size={14} /></button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className="p-1 hover:text-slate-700 disabled:opacity-30" title="Move down"><ChevronDown size={14} /></button>
          <button type="button" onClick={onDuplicate} className="p-1 hover:text-slate-700" title="Duplicate"><Copy size={14} /></button>
          <button type="button" onClick={onDelete} className="p-1 hover:text-red-500" title="Delete"><Trash2 size={14} /></button>
          <button type="button" onClick={onToggleCollapse} className="p-1 hover:text-slate-700" title={collapsed ? 'Expand' : 'Collapse'}>
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* MATCH */}
          <div className="space-y-3">
            <div className="flex items-center gap-1 text-[11px] text-slate-400">
              <Info size={12} /> All conditions must hold (AND). Leave everything empty to match any error.
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input type="checkbox" checked={!!match.transient} onChange={(e) => setMatch({ transient: e.target.checked || undefined })} />
              Transient (infra) failure
              <span title="5xx / 408 / 429 / connection failure / DB transient — classified by the executor."><Info size={12} className="text-slate-400" /></span>
            </label>

            <div>
              <label className={LABEL_CLASS}>Status codes</label>
              <ChipInput
                values={match.statusCodes || []}
                onChange={(v) => setMatch({ statusCodes: v.length ? v : undefined })}
                placeholder="409, 500-599, 40P01…"
                numeric
                validate={(v) => classifyStatusToken(v).valid}
                presets={STATUS_PRESETS}
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>Error types</label>
              <ChipInput
                values={match.exceptionTypes || []}
                onChange={(v) => setMatch({ exceptionTypes: v.length ? (v as string[]) : undefined })}
                placeholder="Aggregate.NotFound, *.VersionMismatch…"
                presets={EXCEPTION_SUGGESTIONS.map(s => ({ label: s, value: s }))}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox" checked={!!match.bodyPredicate} onChange={(e) => toggleBodyPredicate(e.target.checked)} />
                Body predicate
              </label>
              {match.bodyPredicate && (
                <div className="mt-2 space-y-2 pl-1">
                  <div className="flex gap-2">
                    <select
                      value={match.bodyPredicate.kind}
                      onChange={(e) => setMatch({ bodyPredicate: { ...match.bodyPredicate!, kind: e.target.value as any } })}
                      className={INPUT_CLASS}
                    >
                      <option value="jsonpath">jsonpath</option>
                      <option value="xpath">xpath</option>
                      <option value="regex">regex</option>
                    </select>
                    <select
                      value={match.bodyPredicate.op}
                      onChange={(e) => setMatch({ bodyPredicate: { ...match.bodyPredicate!, op: e.target.value as any } })}
                      className={INPUT_CLASS}
                    >
                      <option value="exists">exists</option>
                      <option value="equals">equals</option>
                      <option value="matches">matches</option>
                    </select>
                  </div>
                  <input
                    value={match.bodyPredicate.expression}
                    onChange={(e) => setMatch({ bodyPredicate: { ...match.bodyPredicate!, expression: e.target.value } })}
                    placeholder={match.bodyPredicate.kind === 'jsonpath' ? '$.error.code' : match.bodyPredicate.kind === 'xpath' ? '//fault/code' : 'mailbox.*full'}
                    className={INPUT_CLASS}
                  />
                  {(match.bodyPredicate.op === 'equals' || match.bodyPredicate.op === 'matches') && (
                    <input
                      value={match.bodyPredicate.value || ''}
                      onChange={(e) => setMatch({ bodyPredicate: { ...match.bodyPredicate!, value: e.target.value } })}
                      placeholder={match.bodyPredicate.op === 'equals' ? 'expected value' : 'regex value'}
                      className={INPUT_CLASS}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ACTION */}
          <div className="space-y-3">
            <label className={LABEL_CLASS}>Disposition</label>
            <div className="flex gap-2">
              {(['retry', 'ignore', 'throw'] as const).map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => changeDisposition(d)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border capitalize ${action.disposition === d ? 'bg-teal-50 border-teal-300 text-teal-700 dark:bg-teal-900/20 dark:border-teal-700 dark:text-teal-300' : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300'}`}
                >
                  {d}
                </button>
              ))}
            </div>

            {action.disposition === 'retry' && (
              <div className="space-y-3">
                <div>
                  <label className={LABEL_CLASS}>Max attempts <span className="normal-case font-normal text-slate-400">(0 = retry forever)</span></label>
                  <input type="number" min={0} value={action.maxAttempts}
                    onChange={(e) => setMaxAttempts(Math.max(0, Number(e.target.value) || 0))}
                    className={INPUT_CLASS} />
                </div>

                <button type="button" onClick={() => { setShowBackoff(s => !s); if (!action.backoff) setBackoff({}); }}
                  className="text-xs text-teal-600 hover:underline">
                  {showBackoff ? '▾' : '▸'} Backoff (advanced)
                </button>
                {showBackoff && action.backoff && (
                  <div className="grid grid-cols-2 gap-2 pl-1">
                    <div>
                      <label className="text-[10px] text-slate-400">base ms</label>
                      <input type="number" value={action.backoff.baseMs} onChange={(e) => setBackoff({ baseMs: Number(e.target.value) })} className={INPUT_CLASS} />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-400">× factor</label>
                      <input type="number" value={action.backoff.factor} onChange={(e) => setBackoff({ factor: Number(e.target.value) })} className={INPUT_CLASS} />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-400">cap</label>
                      <select value={action.backoff.capMs} onChange={(e) => setBackoff({ capMs: Number(e.target.value) })} className={INPUT_CLASS}>
                        {BACKOFF_CAP_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        {!BACKOFF_CAP_PRESETS.some(p => p.value === action.backoff!.capMs) && (
                          <option value={action.backoff.capMs}>{action.backoff.capMs} ms</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-400">jitter (0–1)</label>
                      <input type="number" step={0.05} min={0} max={0.99} value={action.backoff.jitter} onChange={(e) => setBackoff({ jitter: Number(e.target.value) })} className={INPUT_CLASS} />
                    </div>
                  </div>
                )}

                {action.maxAttempts > 0 && (
                  <div className="space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 p-2">
                    <label className={LABEL_CLASS}>On exhausted</label>
                    <div className="flex gap-2">
                      {(['ignore', 'throw'] as const).map(d => (
                        <button key={d} type="button"
                          onClick={() => setOnExhausted(d === 'ignore' ? { disposition: 'ignore' } : { disposition: 'throw', errorCode: '' })}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium border capitalize ${action.onExhausted?.disposition === d ? 'bg-teal-50 border-teal-300 text-teal-700 dark:bg-teal-900/20 dark:border-teal-700' : 'border-slate-200 dark:border-slate-700 text-slate-500'}`}>
                          {d}
                        </button>
                      ))}
                    </div>
                    {action.onExhausted?.disposition === 'ignore' && (
                      <input value={action.onExhausted.captureVariable || ''} placeholder="Capture to variable (optional)"
                        onChange={(e) => setOnExhausted({ disposition: 'ignore', captureVariable: e.target.value || undefined })}
                        className={INPUT_CLASS} />
                    )}
                    {action.onExhausted?.disposition === 'throw' && (
                      <input value={action.onExhausted.errorCode} placeholder="Error code (e.g. RetriesExhausted)"
                        onChange={(e) => setOnExhausted({ disposition: 'throw', errorCode: e.target.value })}
                        className={INPUT_CLASS} />
                    )}
                  </div>
                )}
              </div>
            )}

            {action.disposition === 'ignore' && (
              <div>
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={action.captureVariable !== undefined}
                    onChange={(e) => setAction(e.target.checked ? { disposition: 'ignore', captureVariable: 'errorInfo' } : { disposition: 'ignore' })} />
                  Capture error to variable
                </label>
                {action.captureVariable !== undefined && (
                  <input value={action.captureVariable} onChange={(e) => setAction({ disposition: 'ignore', captureVariable: e.target.value })}
                    placeholder="errorInfo" className={`${INPUT_CLASS} mt-1`} />
                )}
              </div>
            )}

            {action.disposition === 'throw' && (
              <div>
                <label className={LABEL_CLASS}>Error code</label>
                <input value={action.errorCode} onChange={(e) => setAction({ disposition: 'throw', errorCode: e.target.value })}
                  placeholder="Conflict" className={INPUT_CLASS} />
                <p className="text-[10px] text-slate-400 mt-1">An Error Boundary Event keyed to this code catches it.</p>
              </div>
            )}
          </div>

          {/* inline errors */}
          {errors.length > 0 && (
            <div className="lg:col-span-2 text-xs text-red-600 dark:text-red-400 space-y-0.5">
              {errors.map((e, i) => <div key={i} className="flex items-center gap-1"><AlertCircle size={12} /> {e.message}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
